import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, Circle } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 60_000; // refresh notifications once a minute

/**
 * Notification bell — small dropdown surfacing the user's in-app notifications.
 * Consumes the existing db.notifications collection that the weekly blog
 * automation cron and other services already write to.
 */
const NotificationBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/notifications`);
      setItems(Array.isArray(data?.notifications) ? data.notifications : []);
    } catch { /* silent — bell stays empty */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    const onClick = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => { clearInterval(t); document.removeEventListener('mousedown', onClick); };
  }, []);

  const unread = items.filter((n) => !n.read).length;

  const handleClick = async (n) => {
    setOpen(false);
    try { await axios.post(`${API_URL}/api/notifications/${n.id}/read`); } catch {}
    setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, read: true } : x));
    if (n.url) navigate(n.url);
  };

  const markAll = async () => {
    try { await axios.post(`${API_URL}/api/notifications/read-all`); } catch {}
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
  };

  return (
    <div ref={popRef} style={{ position: 'relative' }} data-testid="notification-bell-root">
      <button onClick={() => setOpen((v) => !v)} data-testid="notification-bell-btn"
        aria-label={unread ? `${unread} unread notifications` : 'Notifications'}
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          width: 36, height: 36, borderRadius: 10, position: 'relative',
          display: 'grid', placeItems: 'center', color: 'rgba(245,240,232,0.7)',
        }}>
        <Bell size={18} weight={unread ? 'fill' : 'regular'} />
        {unread > 0 && (
          <span data-testid="notification-bell-badge" style={{
            position: 'absolute', top: 6, right: 6,
            minWidth: 16, height: 16, padding: '0 4px', borderRadius: 8,
            background: '#c8f135', color: '#04050a',
            fontFamily: 'ui-monospace, monospace', fontSize: 9, fontWeight: 800,
            display: 'grid', placeItems: 'center', border: '1.5px solid #0a0a0a',
          }}>{unread > 9 ? '9+' : unread}</span>
        )}
      </button>

      {open && (
        <div data-testid="notification-popover" style={{
          position: 'absolute', top: 44, right: 0, width: 340, maxWidth: 'calc(100vw - 24px)',
          background: '#141414', border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 14, padding: 8, zIndex: 200,
          boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px 10px' }}>
            <span style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 14, color: '#f0f0e8' }}>
              Notifications
            </span>
            {unread > 0 && (
              <button onClick={markAll} data-testid="notification-mark-all-read"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                  color: '#c8f135', fontFamily: 'ui-monospace, monospace', fontSize: 10,
                  letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700 }}>
                Mark all read
              </button>
            )}
          </div>
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {items.length === 0 ? (
              <p data-testid="notification-empty" style={{
                padding: '24px 14px', fontFamily: 'ui-monospace, monospace',
                fontSize: 12, color: 'rgba(245,240,232,0.45)', textAlign: 'center', lineHeight: 1.7,
              }}>
                You&apos;re all caught up.<br/>New activity will land here.
              </p>
            ) : items.slice(0, 12).map((n) => (
              <button key={n.id || n.created_at} onClick={() => handleClick(n)}
                data-testid={`notification-item-${n.id || ''}`}
                style={{ width: '100%', textAlign: 'left', background: 'transparent',
                  border: 'none', cursor: 'pointer', padding: '12px 14px', borderRadius: 10,
                  display: 'flex', gap: 10, alignItems: 'flex-start',
                  transition: 'background 0.15s' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                <span style={{ flexShrink: 0, marginTop: 6 }}>
                  <Circle size={7} weight="fill" color={n.read ? 'rgba(255,255,255,0.15)' : '#c8f135'} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700,
                    fontSize: 13, color: '#f0f0e8', letterSpacing: '-0.005em',
                    lineHeight: 1.3, marginBottom: 3 }}>{n.title}</p>
                  <p style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11.5,
                    color: 'rgba(245,240,232,0.55)', lineHeight: 1.55,
                    overflow: 'hidden', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2,
                  }}>{n.message}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
